import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { AuthContext } from "./AuthContext";
import { API_URL } from "../../Constants";
import LiveCallPopup from "./LiveCallPopup";

const IncomingModeContext = createContext(null);

const OWNER_LEVEL_ROLES = ["COMPANY", "SUPER_SUPER_ADMIN", "SUPER_ADMIN", "RESELLER"];
const isOwnerLevel = (accountType) => OWNER_LEVEL_ROLES.includes(accountType);

const decodeJwtPayload = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const getUserIdFromToken = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  return payload?.userId || payload?.id || payload?.sub || null;
};

const INCOMING_MODE_STORAGE_KEY = "incomingModeOn";
const INCOMING_MODE_PATH = "/dashboard/incoming-mode";
const COMPLETED_LINGER_MS = 20000;
const MISSED_LINGER_MS = 45000;
const IDLE_TIMEOUT_MS = 3 * 60 * 1000;
const CONNECTED_TIMEOUT_MS = 2 * 60 * 60 * 1000;

const lastTenDigits = (value) => String(value || "").replace(/\D/g, "").slice(-10);

const sameCaller = (a, b) => {
  const left = lastTenDigits(a);
  return Boolean(left) && left === lastTenDigits(b);
};

const formatTalkTime = (seconds) => {
  const safe = Math.max(0, Math.round(seconds || 0));
  const minutes = Math.floor(safe / 60);
  const rest = String(safe % 60).padStart(2, "0");
  return `${minutes}:${rest}`;
};

const makeStep = (id, label, detail, tone, at) => ({ id, label, detail, tone, at });

const settleSteps = (steps) =>
  steps.map((step) => (step.tone === "live" ? { ...step, tone: "ok" } : step));

const appendStep = (steps, step) =>
  steps.some((existing) => existing.id === step.id) ? steps : [...steps, step];

const describeFailure = (reason) => {
  const text = String(reason || "");
  if (/busy/i.test(text)) {
    return { label: "Line busy", detail: "Your phone was busy" };
  }
  if (/timeout|no answer/i.test(text)) {
    return { label: "Not picked up", detail: "Your phone didn't answer in time" };
  }
  return { label: "Attempt failed", detail: text || null };
};

const blankCall = (phone, now) => ({
  key: `${lastTenDigits(phone) || "call"}-${now}`,
  phone: phone || null,
  name: null,
  leadId: null,
  projectName: null,
  executiveName: null,
  stage: "ringing",
  attempts: 1,
  startedAt: now,
  connectedAt: null,
  endedAt: null,
  durationSeconds: null,
  steps: [makeStep("received", "Call received", null, "ok", now)],
});

const applyCallEvent = (prev, payload, now) => {
  const event = payload?.event || (payload?.leadId ? "CALL_ANSWERED" : null);
  if (!event) return prev;

  const phone = payload.clientCallerId || payload.phone || null;
  const finished = !prev || prev.stage === "completed" || prev.stage === "missed";
  const sameCall = Boolean(prev) && !finished && sameCaller(prev.phone, phone);

  if (prev && !finished && !sameCall && prev.stage === "connected") {
    return prev;
  }

  if (!sameCall && event !== "CALL_RINGING" && event !== "CALL_ANSWERED") {
    return prev;
  }

  const base = sameCall ? prev : blankCall(phone, now);

  if (event === "CALL_RINGING") {
    const name = payload.clientName || base.name;
    const executiveName = payload.executiveName || base.executiveName;

    if (sameCall) {
      const attempts = base.attempts + 1;
      return {
        ...base,
        name,
        executiveName,
        stage: "ringing",
        attempts,
        steps: appendStep(
          settleSteps(base.steps),
          makeStep(`ring-${attempts}`, "Ringing again", `Attempt ${attempts}`, "live", now),
        ),
      };
    }

    return {
      ...base,
      name,
      executiveName,
      steps: appendStep(
        base.steps,
        makeStep("ring-1", "Ringing on your phone", "Attempt 1", "live", now),
      ),
    };
  }

  if (event === "CALL_ATTEMPT_FAILED") {
    const failure = describeFailure(payload.reason);
    return {
      ...base,
      stage: "noanswer",
      steps: appendStep(
        settleSteps(base.steps),
        makeStep(`failed-${base.attempts}`, failure.label, failure.detail, "warn", now),
      ),
    };
  }

  if (event === "CALL_ANSWERED") {
    return {
      ...base,
      stage: "connected",
      leadId: payload.leadId || base.leadId,
      name: payload.leadName || base.name,
      phone: payload.phone || base.phone,
      projectName: payload.projectName || base.projectName,
      executiveName: payload.executiveName || base.executiveName,
      connectedAt: now,
      steps: appendStep(
        settleSteps(base.steps),
        makeStep(
          "connected",
          "Connected",
          payload.statusName ? `Lead status: ${payload.statusName}` : null,
          "live",
          now,
        ),
      ),
    };
  }

  if (event === "CALL_ENDED") {
    const answered = Boolean(payload.answered) || base.stage === "connected";
    const duration =
      payload.durationSeconds ??
      (base.connectedAt ? Math.round((now - base.connectedAt) / 1000) : null);

    const closingStep = answered
      ? makeStep(
          "ended",
          "Call ended",
          duration !== null ? `Talk time ${formatTalkTime(duration)}` : null,
          "ok",
          now,
        )
      : makeStep("missed", "Caller hung up", "Nobody picked up", "bad", now);

    return {
      ...base,
      stage: answered ? "completed" : "missed",
      leadId: payload.leadId || base.leadId,
      name: payload.leadName || base.name,
      phone: payload.phone || base.phone,
      projectName: payload.projectName || base.projectName,
      endedAt: now,
      durationSeconds: duration,
      steps: appendStep(settleSteps(base.steps), closingStep),
    };
  }

  return prev;
};

export const IncomingModeProvider = ({ children }) => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [isIncomingModeOn, setIsIncomingModeOn] = useState(
    () => localStorage.getItem(INCOMING_MODE_STORAGE_KEY) === "true",
  );
  const [connectionState, setConnectionState] = useState("disconnected");
  const [activeCall, setActiveCall] = useState(null);

  const clientRef = useRef(null);
  const activeCallRef = useRef(null);
  const hideTimerRef = useRef(null);

  const stableUser = useMemo(
    () => ({
      id: user?.id,
      accountType: user?.accountType,
      companyId: user?.companyId,
    }),
    [user],
  );

  const companyId = useMemo(
    () =>
      isOwnerLevel(stableUser.accountType) ? stableUser.id : stableUser.companyId,
    [stableUser],
  );

  const executiveId = useMemo(
    () => getUserIdFromToken() || stableUser.id,
    [stableUser.id],
  );

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const dismissIncomingCall = useCallback(() => {
    clearHideTimer();
    activeCallRef.current = null;
    setActiveCall(null);
  }, [clearHideTimer]);

  const toggleIncomingMode = useCallback(() => {
    setIsIncomingModeOn((prev) => {
      const next = !prev;
      localStorage.setItem(INCOMING_MODE_STORAGE_KEY, String(next));
      if (!next) {
        dismissIncomingCall();
      }
      return next;
    });
  }, [dismissIncomingCall]);

  const handleCallEvent = useCallback(
    (payload) => {
      const next = applyCallEvent(activeCallRef.current, payload, Date.now());
      if (next === activeCallRef.current) return;

      activeCallRef.current = next;
      setActiveCall(next);

      clearHideTimer();
      let delay = IDLE_TIMEOUT_MS;
      if (next.stage === "completed") delay = COMPLETED_LINGER_MS;
      else if (next.stage === "missed") delay = MISSED_LINGER_MS;
      else if (next.stage === "connected") delay = CONNECTED_TIMEOUT_MS;

      hideTimerRef.current = setTimeout(() => {
        activeCallRef.current = null;
        setActiveCall(null);
      }, delay);
    },
    [clearHideTimer],
  );

  useEffect(() => {
    if (!isIncomingModeOn || !isAuthenticated || !companyId || !executiveId) {
      if (clientRef.current) {
        clientRef.current.deactivate();
        clientRef.current = null;
      }
      setConnectionState("disconnected");
      return;
    }

    setConnectionState("connecting");

    const client = new Client({
      webSocketFactory: () =>
        new SockJS(
          `${API_URL}/ws/live-calls?executiveId=${executiveId}&companyId=${companyId}`,
        ),
      reconnectDelay: 5000,
      onConnect: () => {
        setConnectionState("live");
        client.subscribe("/user/queue/live-call", (message) => {
          try {
            handleCallEvent(JSON.parse(message.body));
          } catch (err) {
            console.error("Failed to parse live-call payload:", err);
          }
        });
      },
      onWebSocketClose: () => setConnectionState("disconnected"),
      onStompError: () => setConnectionState("disconnected"),
    });

    clientRef.current = client;
    client.activate();

    return () => {
      client.deactivate();
      clientRef.current = null;
    };
  }, [isIncomingModeOn, isAuthenticated, companyId, executiveId, handleCallEvent]);

  useEffect(() => () => clearHideTimer(), [clearHideTimer]);

  const openLead = useCallback(() => {
    navigate(INCOMING_MODE_PATH);
  }, [navigate]);

  const popupCall = isIncomingModeOn ? activeCall : null;
  const onIncomingModePage = location.pathname === INCOMING_MODE_PATH;

  return (
    <IncomingModeContext.Provider
      value={{
        isIncomingModeOn,
        toggleIncomingMode,
        connectionState,
        incomingCall: activeCall,
        activeCall,
        dismissIncomingCall,
      }}
    >
      {children}

      <LiveCallPopup
        call={popupCall}
        onDismiss={dismissIncomingCall}
        onOpenLead={onIncomingModePage ? null : openLead}
      />
    </IncomingModeContext.Provider>
  );
};

export const useIncomingMode = () => {
  const ctx = useContext(IncomingModeContext);
  if (!ctx) {
    throw new Error("useIncomingMode must be used within an IncomingModeProvider");
  }
  return ctx;
};
